import {
	type PoExport,
	type PoPublisherTab,
	poCreateExport,
	poGetExportDownload,
	poListExports
} from '@repo/sdk/gen'
import { Badge } from '@repo/ui/badge'
import { Button } from '@repo/ui/button'
import { AsyncSelect } from '@repo/ui/commons/async-pickers'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@repo/ui/dialog'
import { Spinner } from '@repo/ui/spinner'
import { downloadFile } from '@repo/ui/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CircleCheck, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import i18n, { type TKey, useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/lib/query-keys'
import { poErrorMessage } from '@/routes/(private)/documents/-lib/error-message'
import { MrBookImportSteps } from './mrbook-import-instructions'

export const EXPORT_KIND_KEYS = {
	report: 'po:export.report_kind',
	mrbook: 'po:export.mrbook_kind'
} as const satisfies Record<PoExport['kind'], TKey>

const DOWNLOAD_POLL_MS = 1500
const DOWNLOAD_POLL_MAX = 40
// A generation that fails is retried once by the backend a few seconds later,
// and a retry that succeeds turns the export completed: a failure is final
// only once it has lasted this many polls.
const FAILED_POLLS_MAX = 10
// What the expert is told once the wait is over, by where the export stands.
const WAIT_OUTCOMES = {
	completed: { tone: 'success', key: 'po:export.ready' },
	failed: { tone: 'error', key: 'po:export.failed' },
	pending: { tone: 'info', key: 'po:export.still_generating' }
} as const satisfies Record<
	PoExport['status'],
	{ tone: 'success' | 'error' | 'info'; key: TKey }
>

// The grid filters the export mirrors — same vocabulary as the toolbar.
interface ExportFilters {
	search?: string
	publisher?: number
	distributor?: string
	tipologia?: 'elementari' | 'medie' | 'superiori' | 'varia'
	review?: 'pending' | 'accepted' | 'corrected'
}

// Polls the export list (which carries the job status) until this export is
// completed, then fetches the download links once. Polling the status list
// avoids hammering the download endpoint with 404s while the job runs.
// Resolves to where the export stands when the wait ends.
async function waitAndDownload(
	runId: string,
	exportId: string,
	kind: PoExport['kind'] = 'report'
) {
	let failedPolls = 0
	for (let attempt = 0; attempt < DOWNLOAD_POLL_MAX; attempt++) {
		const list = await poListExports({
			body: { page: 1, limit: 20, filters: { runId } }
		})
		const record = list.data.find((entry) => entry.id === exportId)
		if (record?.status === 'completed') {
			await downloadExport(exportId, kind)
			return 'completed' as const
		}
		if (record?.status === 'failed') {
			failedPolls++
			if (failedPolls >= FAILED_POLLS_MAX) {
				if (record.error) {
					throw new Error(record.error)
				}
				return 'failed' as const
			}
		} else {
			failedPolls = 0
		}
		await new Promise((resolve) => setTimeout(resolve, DOWNLOAD_POLL_MS))
	}
	return 'pending' as const
}

async function downloadExport(exportId: string, kind: PoExport['kind']) {
	const download = await poGetExportDownload({
		body: { filters: { id: exportId } }
	})
	const url =
		kind === 'mrbook' ? download.data.mrbookUrl : download.data.xlsxUrl
	if (!url) {
		throw new Error(i18n.t('po:export.download_unavailable'))
	}
	downloadFile(url)
}

// The export follows the grid's active filters ("l'ordine si fa per
// distributore": no dedicated publisher picker). Filters can be removed here
// before generating, never edited — editing belongs to the toolbar.
function ExportDialog(props: {
	runId: string
	filters: ExportFilters
	publishers: PoPublisherTab[]
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	// Keep the active export outside the popup, which unmounts when dismissed.
	// Reset a finished workflow only after its closing animation has completed.
	const resetAfterClose = useRef(false)

	const createExport = useMutation({
		mutationFn: async (draft: {
			kind: PoExport['kind']
			filters: ExportFilters
		}) => {
			const kind = draft.kind
			const filters = draft.filters
			const created = await poCreateExport({
				body: {
					data: {
						kind,
						runId: props.runId,
						search: filters.search || undefined,
						publisherIds:
							filters.publisher === undefined
								? undefined
								: [filters.publisher],
						distributorNames: filters.distributor
							? [filters.distributor]
							: undefined,
						schoolTypes: filters.tipologia
							? [filters.tipologia]
							: undefined,
						reviewStatus:
							kind === 'report' && filters.review
								? [filters.review]
								: undefined
					}
				}
			})
			const status = await waitAndDownload(
				props.runId,
				created.data.id,
				kind
			)
			return { status, id: created.data.id, kind }
		},
		onSuccess: (result) => {
			queryClient.invalidateQueries({
				queryKey: makeQueryKeys.po.exportsAll()
			})
			const outcome = WAIT_OUTCOMES[result.status]
			toast[outcome.tone](t(outcome.key))
		},
		// A refusal leaves nothing behind, but an unreachable queue leaves the
		// export just created as failed: the history shows it.
		onError: async (error) => {
			toast.error(poErrorMessage(error))
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.po.exportsAll()
			})
		}
	})

	const changeOpen = (open: boolean) => {
		resetAfterClose.current = !(open || createExport.isPending)
		props.onOpenChange(open)
	}

	return (
		<Dialog
			onOpenChange={changeOpen}
			onOpenChangeComplete={(open) => {
				if (!open && resetAfterClose.current) {
					createExport.reset()
				}
			}}
			open={props.open}
		>
			<DialogContent className='max-h-[calc(100dvh-2rem)] overflow-y-auto'>
				{createExport.isPending && <ExportDownloadWaiting />}
				{createExport.isSuccess &&
					createExport.data.status === 'completed' && (
						<ExportDownloadSuccess kind={createExport.data.kind} />
					)}
				{(createExport.isError ||
					(createExport.isSuccess &&
						createExport.data.status !== 'completed')) && (
					<ExportDownloadNotice
						failed={
							createExport.isError ||
							createExport.data?.status === 'failed'
						}
						message={
							createExport.isError
								? poErrorMessage(createExport.error)
								: undefined
						}
						onOpenChange={changeOpen}
					/>
				)}
				{createExport.isIdle && (
					<ExportForm
						filters={props.filters}
						onConfirm={(draft) => {
							createExport.mutate(draft)
						}}
						onOpenChange={changeOpen}
						publishers={props.publishers}
					/>
				)}
			</DialogContent>
		</Dialog>
	)
}

// The draft remounts per opening; the generation above survives dismissal.
function ExportForm(props: {
	filters: ExportFilters
	publishers: PoPublisherTab[]
	onOpenChange: (open: boolean) => void
	onConfirm: (draft: {
		kind: PoExport['kind']
		filters: ExportFilters
	}) => void
}) {
	const { t } = useTranslation()
	const [filters, setFilters] = useState(props.filters)
	const [kind, setKind] = useState<PoExport['kind']>('mrbook')

	const publisherName = (id: number) => {
		const match = props.publishers.find((p) => p.publisherId === id)
		return match?.publisherName ?? String(id)
	}

	const chips = [
		filters.search
			? {
					key: 'search' as const,
					label: `${t('po:export.filter_search')}: “${filters.search}”`
				}
			: null,
		filters.publisher !== undefined
			? {
					key: 'publisher' as const,
					label: `${t('po:export.filter_publisher')}: ${publisherName(filters.publisher)}`
				}
			: null,
		filters.distributor
			? {
					key: 'distributor' as const,
					label: `${t('po:export.filter_distributor')}: ${filters.distributor}`
				}
			: null,
		filters.tipologia
			? {
					key: 'tipologia' as const,
					label: `${t('po:export.filter_school_type')}: ${t(`po:school_type.${filters.tipologia}`)}`
				}
			: null,
		kind === 'report' && filters.review
			? {
					key: 'review' as const,
					label: `${t('po:export.filter_review')}: ${t(`po:review.${filters.review}`)}`
				}
			: null
	].filter((chip) => chip !== null)

	return (
		<>
			<DialogHeader>
				<DialogTitle>{t('po:export.title')}</DialogTitle>
				<DialogDescription>
					{t(
						kind === 'mrbook'
							? 'po:export.mrbook_description'
							: 'po:export.description'
					)}
				</DialogDescription>
			</DialogHeader>
			<AsyncSelect
				aria-label={t('po:export.kind')}
				onValueChange={(value) => {
					if (value) {
						setKind(value)
					}
				}}
				required
				source={{
					items: ['mrbook', 'report'] as const,
					getKey: (value) => value,
					getLabel: (value) => t(EXPORT_KIND_KEYS[value])
				}}
				value={kind}
			/>
			<div className='flex flex-col gap-2'>
				<span className='font-medium text-sm'>
					{t('po:export.active_filters')}
				</span>
				{chips.length > 0 ? (
					<div className='flex flex-wrap gap-1.5'>
						{chips.map((chip) => (
							<Badge
								className='h-6 gap-1 pr-1'
								key={chip.key}
								variant='secondary'
							>
								{chip.label}
								<button
									aria-label={t('po:export.remove_filter', {
										filter: chip.label
									})}
									className='rounded-full p-0.5 transition-colors hover:bg-foreground/10'
									onClick={() =>
										setFilters((prev) => ({
											...prev,
											[chip.key]: undefined
										}))
									}
									type='button'
								>
									<X className='size-3' />
								</button>
							</Badge>
						))}
					</div>
				) : (
					<p className='text-muted-foreground text-sm'>
						{t(
							kind === 'mrbook'
								? 'po:export.mrbook_no_filters'
								: 'po:export.no_filters'
						)}
					</p>
				)}
			</div>
			<DialogFooter>
				<Button
					onClick={() => props.onOpenChange(false)}
					variant='outline'
				>
					{t('common:cancel')}
				</Button>
				<Button onClick={() => props.onConfirm({ kind, filters })}>
					{t('po:export.generate')}
				</Button>
			</DialogFooter>
		</>
	)
}

function ExportDownloadWaiting() {
	const { t } = useTranslation()
	return (
		<div
			aria-live='polite'
			className='flex flex-col items-center gap-6 pt-6 text-center'
		>
			<Spinner
				aria-label={t('po:export.waiting_title')}
				className='size-10 text-primary motion-reduce:animate-none'
			/>
			<DialogHeader className='items-center'>
				<DialogTitle>{t('po:export.waiting_title')}</DialogTitle>
				<DialogDescription>
					{t('po:export.waiting_description')}
				</DialogDescription>
			</DialogHeader>
		</div>
	)
}

function ExportDownloadSuccess(props: { kind: PoExport['kind'] }) {
	const { t } = useTranslation()
	return (
		<>
			<DialogHeader>
				<DialogTitle
					aria-live='polite'
					className='flex items-center gap-2 font-bold text-emerald-700 text-lg'
				>
					<CircleCheck
						aria-hidden='true'
						className='size-6 shrink-0'
						strokeWidth={2.5}
					/>
					{t('po:export.download_started')}
				</DialogTitle>
				<DialogDescription className='text-foreground'>
					{t(
						props.kind === 'mrbook'
							? 'po:export.success_title'
							: 'po:export.report_ready'
					)}
				</DialogDescription>
			</DialogHeader>
			{props.kind === 'mrbook' ? (
				<MrBookImportSteps />
			) : (
				<p className='text-muted-foreground text-sm'>
					{t('po:export.report_ready_description')}
				</p>
			)}
		</>
	)
}

function ExportDownloadNotice(props: {
	failed: boolean
	message?: string
	onOpenChange: (open: boolean) => void
}) {
	const { t } = useTranslation()
	return (
		<>
			<DialogHeader>
				<DialogTitle>
					{t(
						props.failed
							? 'po:export.failed_title'
							: 'po:export.delayed_title'
					)}
				</DialogTitle>
				<DialogDescription>
					{props.message ??
						t(
							props.failed
								? 'po:export.failed'
								: 'po:export.still_generating'
						)}
				</DialogDescription>
			</DialogHeader>
			{props.failed && (
				<p className='text-muted-foreground text-sm'>
					{t('po:export.check_history')}
				</p>
			)}
			<DialogFooter>
				<Button onClick={() => props.onOpenChange(false)}>
					{t('po:export.understood')}
				</Button>
			</DialogFooter>
		</>
	)
}

export { ExportDialog, waitAndDownload }
